import { useMutation } from '@tanstack/react-query';
import { History, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import type { PageContent } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EmptyState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime, useTz } from './tz';
import type { PageVersion } from './types';

export function HistoryTab({ pageId, versions, dirty, onRestored }: { pageId: string; versions: PageVersion[]; dirty: boolean; onRestored: (r: { draftVersion: number; draft: PageContent }) => void }) {
  const tz = useTz();
  const restore = useMutation({
    mutationFn: (versionId: string) => api.post<{ draftVersion: number; draft: PageContent }>(`/site/pages/${pageId}/restore/${versionId}`),
    onSuccess: (r) => {
      toast.success('Versão restaurada no rascunho. Publique para colocá-la no ar.');
      onRestored(r);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (versions.length === 0)
    return <EmptyState title="Nada publicado ainda" description="Cada publicação cria uma versão que você pode restaurar depois." />;
  return (
    <div className="grid gap-3">
      <p className="text-sm text-muted-foreground">Restaurar copia a versão para o rascunho; o site só muda quando você publicar.</p>
      <ul className="grid gap-2">
        {versions.map((v) => (
          <li key={v.id} className="flex items-center gap-3 rounded-md border p-3">
            <History className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                Versão {v.versionNumber} {v.current && <Badge className="ml-1" variant="secondary">No ar</Badge>}
              </p>
              <p className="text-xs text-muted-foreground">{fmtDateTime(v.createdAt, tz)}</p>
            </div>
            <ConfirmDialog
              destructive={false}
              trigger={<Button type="button" size="sm" variant="outline"><RotateCcw aria-hidden /> Restaurar</Button>}
              title={`Restaurar a versão ${v.versionNumber}?`}
              description={dirty ? 'Há alterações não salvas no rascunho: elas serão substituídas por esta versão.' : 'O rascunho atual será substituído por esta versão.'}
              confirmLabel="Restaurar versão"
              onConfirm={() => restore.mutateAsync(v.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
